/**
 * Sound Effects Constants
 *
 * Constants for AI sound effects generation providers.
 * Follows patterns from videoSources.ts and viduVideo.ts
 */

import sharedConstants from './shared_constants.json';

// `usdToTokens` and `formatTokens` were here and are gone with
// `calculateSfxCost`, their only caller: a private copy of the markup (1.5) and
// of the USD->token conversion is the second implementation of a PRICE, and
// this file no longer computes one. The server's quote arrives already in
// tokens (POST /api/v1/cost/preview) and is rendered by
// components/tokens/CloudLocalCostDisplay, which is the one cost cell.

// =============================================================================
// PROVIDER IDS
// =============================================================================

export const SFX_PROVIDERS = {
  HUNYUAN_FOLEY: 'hunyuan_foley',
  LTX2_AUDIO: 'ltx2_audio',
  AUDIOCRAFT: 'audiocraft',
  STABLE_AUDIO: 'stable_audio',
  // Local providers
  AUDIOCRAFT_LOCAL: 'audiocraft_local',
  STABLE_AUDIO_LOCAL: 'stable_audio_local',
  HUNYUAN_FOLEY_LOCAL: 'hunyuan_foley_local',
  LTX2_AUDIO_LOCAL: 'ltx2_audio_local',
} as const;

export type SfxProvider = (typeof SFX_PROVIDERS)[keyof typeof SFX_PROVIDERS];

// =============================================================================
// GENERATION TYPES
// =============================================================================

export const SFX_TYPES = {
  TEXT_TO_AUDIO: 't2a',
  VIDEO_TO_AUDIO: 'v2a',
  UNIFIED_AV: 'unified_av',
} as const;

export type SfxGenerationType = (typeof SFX_TYPES)[keyof typeof SFX_TYPES];

// =============================================================================
// QUALITY TIERS
// =============================================================================

export type SfxQualityTier = 'good' | 'great' | 'excellent' | 'premium';

/**
 * Quality stars (1-5) per model, from the backend's SFX_QUALITY_RATINGS
 * (constants/pricing/sound_effects.py). Never hardcode stars in a UI table:
 * the picker used to show Stable Audio as 1 star while the backend rated it 4.
 */
const SFX_QUALITY_RATINGS = sharedConstants.constants.misc.SFX_QUALITY_RATINGS as Record<
  string,
  number
>;

/** Cloud model id behind any provider id ('audiocraft_local' -> 'audiocraft'). */
export function getSfxBaseProvider(provider: string): string {
  return provider.endsWith('_local') ? provider.slice(0, -'_local'.length) : provider;
}

/** Quality stars (1-5) for a provider. A local variant is the SAME model as its cloud twin. */
export function getSfxQualityRating(provider: string): number {
  return SFX_QUALITY_RATINGS[getSfxBaseProvider(provider)] ?? 3;
}

// =============================================================================
// LOCAL PROVIDERS
// =============================================================================
//
// Declared before SFX_PROVIDER_INFO because the "_local" entries there are
// DERIVED from this table (RULE #-2: one definition, no drift). Hardware
// requirements and download sizes deliberately live only in the backend
// (constants/components/sound_effects.py, served per component by
// GET /api/v1/components) — the copies that used to sit here understated every
// model, telling users LTX-2 Audio needed "8GB+ VRAM / 4GB download" when it
// actually needs 24GB of CUDA VRAM and a 38GB download.

export interface SfxLocalProviderInfo {
  name: string;
  /** Component id in the backend registry — must match utils/components/registry.py. */
  componentId: string;
  cloudEquivalent: SfxProvider;
  sampleRate: number;
  maxDuration: number;
  description: string;
  type: SfxGenerationType;
  capabilities: {
    text_to_audio: boolean;
    video_to_audio: boolean;
  };
  badge: string | null;
}

export const SFX_LOCAL_PROVIDERS: Record<string, SfxLocalProviderInfo> = {
  [SFX_PROVIDERS.AUDIOCRAFT_LOCAL]: {
    name: 'AudioCraft (Local)',
    componentId: SFX_PROVIDERS.AUDIOCRAFT_LOCAL,
    cloudEquivalent: SFX_PROVIDERS.AUDIOCRAFT,
    sampleRate: 16000,
    maxDuration: 30, // AUDIOCRAFT_MAX_DURATION
    description: 'Meta AudioGen - Run locally on your GPU',
    type: SFX_TYPES.TEXT_TO_AUDIO,
    capabilities: {
      text_to_audio: true,
      video_to_audio: false,
    },
    badge: null,
  },
  [SFX_PROVIDERS.STABLE_AUDIO_LOCAL]: {
    name: 'Stable Audio (Local)',
    componentId: SFX_PROVIDERS.STABLE_AUDIO_LOCAL,
    cloudEquivalent: SFX_PROVIDERS.STABLE_AUDIO,
    sampleRate: 44100,
    maxDuration: 47, // STABLE_AUDIO_MAX_DURATION
    description: 'Stability AI - Ambient sounds locally',
    type: SFX_TYPES.TEXT_TO_AUDIO,
    capabilities: {
      text_to_audio: true,
      video_to_audio: false,
    },
    badge: null,
  },
  [SFX_PROVIDERS.HUNYUAN_FOLEY_LOCAL]: {
    name: 'HunyuanVideo-Foley (Local)',
    componentId: SFX_PROVIDERS.HUNYUAN_FOLEY_LOCAL,
    cloudEquivalent: SFX_PROVIDERS.HUNYUAN_FOLEY,
    sampleRate: 48000,
    maxDuration: 30, // V2A route cap (duration_seconds le=30)
    description: 'Video-to-audio multimodal diffusion - Local GPU',
    type: SFX_TYPES.VIDEO_TO_AUDIO,
    capabilities: {
      // HunyuanFoleyLocalProvider implements BOTH TextToAudioCapable and
      // VideoToAudioCapable (sound_effects/providers/hunyuan_foley/local_provider.py).
      text_to_audio: true,
      video_to_audio: true,
    },
    badge: 'V2A',
  },
  [SFX_PROVIDERS.LTX2_AUDIO_LOCAL]: {
    name: 'LTX-2 Audio (Local)',
    componentId: SFX_PROVIDERS.LTX2_AUDIO_LOCAL,
    cloudEquivalent: SFX_PROVIDERS.LTX2_AUDIO,
    sampleRate: 24000, // SFX_SAMPLE_RATE[SfxLocalModel.LTX2_AUDIO]
    maxDuration: 10, // LTX2_AUDIO_MAX_DURATION
    description: 'Unified video+audio generation - Fast local generation',
    type: SFX_TYPES.TEXT_TO_AUDIO,
    capabilities: {
      text_to_audio: true,
      video_to_audio: false,
    },
    badge: 'UNIFIED',
  },
};

/**
 * Component ids of the local SFX models, in the backend registry's terms
 * (utils/components/registry.py). Derived from SFX_LOCAL_PROVIDERS so the two
 * lists can never drift; this is what the SFX screens hand to
 * useLocalModelInstalls.
 */
export const SFX_COMPONENT_IDS: readonly string[] = Object.values(SFX_LOCAL_PROVIDERS).map(
  (info) => info.componentId
);

/** Quality tier label for a star rating, so tier and stars can never disagree. */
const SFX_QUALITY_TIER_BY_RATING: Record<number, SfxQualityTier> = {
  1: 'good',
  2: 'good',
  3: 'great',
  4: 'excellent',
  5: 'premium',
};

/** Build the SFX_PROVIDER_INFO entry for a local provider from SFX_LOCAL_PROVIDERS. */
function localProviderInfo(providerId: string): SfxProviderInfo {
  const local = SFX_LOCAL_PROVIDERS[providerId];
  return {
    name: local.name,
    description: 'Runs locally on your GPU - free once installed',
    type: local.type,
    capabilities: local.capabilities,
    sampleRate: local.sampleRate,
    maxDuration: local.maxDuration,
    quality: SFX_QUALITY_TIER_BY_RATING[getSfxQualityRating(providerId)] ?? 'good',
    tokensPer10s: 0,
    badge: 'LOCAL',
    // Local execution is never gated by a cloud *_GENERATION_ENABLED flag —
    // that kill-switch only protects Asgard's own GPUs (see
    // shared/routes/sound_effects.py).
    envVar: '',
  };
}

// =============================================================================
// PROVIDER INFO
// =============================================================================

export interface SfxProviderInfo {
  name: string;
  description: string;
  type: SfxGenerationType;
  capabilities: {
    text_to_audio: boolean;
    video_to_audio: boolean;
  };
  sampleRate: number;
  maxDuration: number;
  quality: SfxQualityTier;
  /**
   * Reference price for a TEN-SECOND clip, shown by the picker as
   * "~90 tokens/10s". It is a label about one duration, not a price for the
   * request: the request's price comes from POST /api/v1/cost/preview, and the
   * USD twin of this field (`costPer10s`) was deleted with `calculateSfxCost`
   * because scaling it by the duration is what disagreed with the charge at
   * 113 of the slider's 117 stops. Checked against the server at 10 s by
   * tests/test_shared/test_sfx_slider_quote_equals_charge_regression.py.
   */
  tokensPer10s: number;
  badge: string | null;
  envVar: string;
}

export const SFX_PROVIDER_INFO: Record<SfxProvider, SfxProviderInfo> = {
  [SFX_PROVIDERS.HUNYUAN_FOLEY]: {
    name: 'HunyuanVideo-Foley',
    description: 'Best quality - multimodal diffusion for T2A and V2A',
    type: SFX_TYPES.VIDEO_TO_AUDIO,
    capabilities: {
      text_to_audio: true,
      video_to_audio: true,
    },
    sampleRate: 48000,
    // 30s: the V2A endpoint caps duration_seconds at 30 and GET /providers
    // reports max_duration 30.0 (shared/routes/sound_effects.py).
    maxDuration: 30,
    quality: 'premium',
    tokensPer10s: 179,
    badge: 'V2A',
    envVar: 'HUNYUAN_FOLEY_GENERATION_ENABLED',
  },
  [SFX_PROVIDERS.LTX2_AUDIO]: {
    name: 'LTX-2 Audio',
    description: 'Unified video+audio - fast generation with synchronized output',
    type: SFX_TYPES.UNIFIED_AV,
    capabilities: {
      text_to_audio: true,
      video_to_audio: false,
    },
    sampleRate: 48000,
    maxDuration: 10,
    quality: 'excellent',
    tokensPer10s: 233,
    badge: 'UNIFIED',
    envVar: 'LTX2_AUDIO_GENERATION_ENABLED',
  },
  [SFX_PROVIDERS.AUDIOCRAFT]: {
    name: 'AudioCraft',
    description: 'Text-to-audio - Meta AudioGen, high quality sound effects',
    type: SFX_TYPES.TEXT_TO_AUDIO,
    capabilities: {
      text_to_audio: true,
      video_to_audio: false,
    },
    sampleRate: 16000,
    maxDuration: 30,
    quality: 'great',
    tokensPer10s: 108,
    badge: null,
    envVar: 'AUDIOCRAFT_GENERATION_ENABLED',
  },
  [SFX_PROVIDERS.STABLE_AUDIO]: {
    name: 'Stable Audio',
    description: 'Text-to-audio - Stability AI, basic ambient audio',
    type: SFX_TYPES.TEXT_TO_AUDIO,
    capabilities: {
      text_to_audio: true,
      video_to_audio: false,
    },
    sampleRate: 44100,
    maxDuration: 47,
    quality: 'good',
    tokensPer10s: 72,
    badge: null,
    envVar: 'STABLE_AUDIO_GENERATION_ENABLED',
  },
  // Local variants are DERIVED from SFX_LOCAL_PROVIDERS: same model, same
  // capabilities and duration limits, only free. Hand-written copies had
  // drifted into direct contradictions (this table claimed ltx2_audio_local
  // was a 47s V2A model; SFX_LOCAL_PROVIDERS said 10s T2A — the backend agrees
  // with the latter).
  [SFX_PROVIDERS.AUDIOCRAFT_LOCAL]: localProviderInfo(SFX_PROVIDERS.AUDIOCRAFT_LOCAL),
  [SFX_PROVIDERS.STABLE_AUDIO_LOCAL]: localProviderInfo(SFX_PROVIDERS.STABLE_AUDIO_LOCAL),
  [SFX_PROVIDERS.HUNYUAN_FOLEY_LOCAL]: localProviderInfo(SFX_PROVIDERS.HUNYUAN_FOLEY_LOCAL),
  [SFX_PROVIDERS.LTX2_AUDIO_LOCAL]: localProviderInfo(SFX_PROVIDERS.LTX2_AUDIO_LOCAL),
};

// =============================================================================
// PROVIDER LIMITS (single resolver for cloud AND local ids)
// =============================================================================

export interface SfxProviderLimits {
  /** Longest clip this provider accepts, in seconds. */
  maxDuration: number;
  /** True when the provider generates audio FOR a video (needs a video upload). */
  requiresVideo: boolean;
}

/**
 * Duration/mode limits for ANY provider id, cloud or local.
 *
 * The panel and the duration slider used to look providers up in the
 * cloud-only SFX_PROVIDER_OPTIONS table: a "_local" id was simply not found,
 * so the slider silently fell back to 10s (capping audiocraft_local at 10s
 * instead of 30s) and hunyuan_foley_local was treated as a text-to-audio
 * provider, making video-to-audio unreachable from the UI.
 */
export function getSfxProviderLimits(provider: string): SfxProviderLimits {
  const info = SFX_PROVIDER_INFO[provider as SfxProvider] as SfxProviderInfo | undefined;
  if (!info) {
    return { maxDuration: 10, requiresVideo: false };
  }
  return {
    maxDuration: info.maxDuration,
    // A provider that can do both (hunyuan_foley) is offered as V2A — that is
    // its reason to exist and matches how the picker groups it.
    requiresVideo: info.capabilities.video_to_audio,
  };
}

/** Human-readable cloud price for a provider, e.g. "~90 tokens/10s". */
export function getSfxPricingLabel(provider: SfxProvider): string {
  const info = SFX_PROVIDER_INFO[provider];
  return info.tokensPer10s > 0 ? `~${info.tokensPer10s} tokens/10s` : 'Free';
}

// =============================================================================
// GENERATION CONFIG
// =============================================================================

export interface SfxGenerationConfig {
  provider: SfxProvider;
  prompt: string;
  duration: number;
  seed?: number;
}

export const DEFAULT_SFX_CONFIG: SfxGenerationConfig = {
  provider: SFX_PROVIDERS.STABLE_AUDIO,
  prompt: '',
  duration: 10,
  seed: -1,
};

// =============================================================================
// PROMPT CATEGORIES (for UI inspiration)
// =============================================================================

export interface SfxPromptCategory {
  id: string;
  label: string;
  icon: string;
  prompts: string[];
}

export const SFX_PROMPT_CATEGORIES: SfxPromptCategory[] = [
  {
    id: 'nature',
    label: 'Nature',
    icon: 'Trees',
    prompts: [
      'rain falling on leaves with distant thunder',
      'birds chirping in a forest at dawn',
      'ocean waves crashing on beach',
      'gentle wind through tall grass',
      'river flowing over rocks',
      'fire crackling in a campfire',
    ],
  },
  {
    id: 'urban',
    label: 'Urban',
    icon: 'Building',
    prompts: [
      'city traffic with car horns',
      'subway train arriving at station',
      'busy restaurant ambience',
      'footsteps on pavement',
      'construction site with heavy machinery',
      'sirens in the distance',
    ],
  },
  {
    id: 'cinematic',
    label: 'Cinematic',
    icon: 'Film',
    prompts: [
      'dramatic explosion with debris',
      'sci-fi spaceship engine hum',
      'sword clash in battle',
      'laser gun shooting with echo',
      'epic orchestral impact hit',
      'suspenseful tension riser',
    ],
  },
  {
    id: 'foley',
    label: 'Foley',
    icon: 'Mic',
    prompts: [
      'wooden door creaking open slowly',
      'glass shattering on hard floor',
      'footsteps on gravel path',
      'paper being crumpled and thrown',
      'keys jingling on keychain',
      'typing on mechanical keyboard',
    ],
  },
];

// =============================================================================
// COST CALCULATION -- deliberately absent
// =============================================================================
//
// `calculateSfxCost(provider, seconds)` used to live here and was deleted, not
// moved. It priced a clip as `costPer10s / 10 * seconds`: a straight line, while
// three of the four providers are billed a flat per-generation fee PLUS a
// per-second rate (`constants/sound_effects/*.py`). The two agreed at 10 s and
// nowhere else -- stable_audio at 1 s displayed 6 tokens against a charge of 33.
//
// The price has ONE implementation, on the server, and the UI asks for it:
// POST /api/v1/cost/preview, via `services/costPreview.ts` / TokenEstimateCard
// (see SfxDurationSlider). `tokensPer10s` below stays because it labels a
// per-10-second REFERENCE price in the provider picker -- it states the duration
// it is true for, and it is checked against the server in
// tests/test_shared/test_sfx_slider_quote_equals_charge_regression.py.
//
// Because it is a copy of a server number, it goes stale when that number
// moves, and the test above is the only thing that says so. It moved in 2026-09
// (150/195/90/60 -> 179/233/108/72) when cost-derived prices were floored at
// MIN_EFFECTIVE_MARKUP_MULTIPLIER so a job stays profitable at the deepest
// volume discount. Re-read these from
// SoundEffectsPricing().estimate_by_provider(provider=..., duration_seconds=10)
// when that test fails -- never hand-adjust them until it passes.

/**
 * Check if a provider is a local provider
 */
export function isLocalProvider(provider: SfxProvider): boolean {
  return provider in SFX_LOCAL_PROVIDERS;
}

/**
 * Get local provider info
 */
export function getLocalProviderInfo(provider: SfxProvider): SfxLocalProviderInfo | null {
  return SFX_LOCAL_PROVIDERS[provider] || null;
}

/**
 * Find another local SFX provider that IS compatible with this machine, to
 * suggest as a one-click swap when the currently selected local provider is
 * not. Never suggest installing something that can never run here — offer a
 * provider that actually works instead (RULE #5, no dead-end fallbacks).
 *
 * @param excludeProvider - the incompatible provider currently selected.
 * @param compatibleByComponentId - component id -> hardware verdict, as
 *   reported by useLocalModelInstalls (GET /api/v1/system/hardware). A missing
 *   entry is treated as compatible — advisory-only, mirrors the hook's own
 *   fallback for when hardware detection itself failed.
 * @returns the highest-quality compatible local provider id, or null when
 *   none of the local models can run on this machine.
 */
export function findCompatibleLocalAlternative(
  excludeProvider: SfxProvider,
  compatibleByComponentId: Record<string, boolean>
): SfxProvider | null {
  const candidates = (Object.keys(SFX_LOCAL_PROVIDERS) as SfxProvider[])
    .filter((id) => id !== excludeProvider)
    .sort((a, b) => getSfxQualityRating(b) - getSfxQualityRating(a));

  for (const id of candidates) {
    const componentId = SFX_LOCAL_PROVIDERS[id].componentId;
    if (compatibleByComponentId[componentId] !== false) {
      return id;
    }
  }
  return null;
}
